import Button from '@atoms/Button/Button';
import Drawer from '@atoms/Drawer/Drawer';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { ITiledSpringboardCard } from '@models/ITiledSpringboardCard';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { KeyboardEvent, MouseEvent, useMemo, useState } from 'react';

type Props = ITiledSpringboardCard & {
  readonly className?: string;
};

const TiledSpringboardCard = ({
  title,
  coverImage,
  tabletCoverImage,
  mobileCoverImage,
  coverVideo,
  content,
  footNote,
  className,
}: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isTablet = useMediaQuery(1440);
  const isMobile = useMediaQuery(768);

  const imageSelect = useMemo(() => {
    if (mobileCoverImage && isMobile) return mobileCoverImage;
    if (tabletCoverImage && isTablet && !isMobile) return tabletCoverImage;
    return coverImage;
  }, [mobileCoverImage, isMobile, tabletCoverImage, isTablet, coverImage]);

  const handleInteraction = (
    e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
  ) => {
    if (!(e.target as HTMLElement).hasAttribute('data-video-control')) {
      setIsDrawerOpen(true);
      updateDataLayer({
        event: 'springboard',
        content_type: 'tile_open',
        content_name: title,
      });
    }
  };

  return (
    <>
      <div
        className={classNames(
          'relative h-[420px] overflow-hidden rounded-[10px] bg-grey-600 md:h-[380px] md:first:h-[480px] xl:h-full xl:first:h-full',
          className
        )}
        onClick={handleInteraction}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleInteraction(e);
          }
        }}
        role="button"
        tabIndex={0}
      >
        {imageSelect && (
          <Image
            image={imageSelect}
            imageClasses="w-full object-cover h-full"
          />
        )}

        {coverVideo && (
          <InViewVideo
            videoClasses="[&>div>video]:object-cover h-full"
            wrapperClasses="h-full"
            autoPlay
            hasInVideoPlayButton
            inVideoPlayButtonPositionClassName="bottom-2.5 left-2.5 md:bottom-5 md:left-5"
            controls={false}
            video={coverVideo}
            dataTestId="Tiled-Springboard-Card"
            playOnCoverClick={false}
          />
        )}

        <p className="h3 absolute top-7.5 right-7.5 left-7.5 text-left text-white xl:top-13 xl:left-13 xl:w-4/6">
          {title}
        </p>

        <Icon
          name="arrow_right"
          size="1.875rem"
          colorClassName="text-yellow"
          className="absolute right-[18px] bottom-[21px] xl:right-[38px] xl:bottom-[41px]"
        />
      </div>

      <Drawer
        setIsOpen={setIsDrawerOpen}
        isOpen={isDrawerOpen}
        onCloseCallback={() => {
          updateDataLayer({
            event: 'springboard',
            content_type: 'tile_close',
            content_name: title,
          });
        }}
      >
        <div className="flex h-full flex-col justify-between overflow-auto px-5 pt-6 pb-10 xl:pt-[52px] xl:pr-[53px] xl:pb-15 xl:pl-[72px]">
          <div className="flex flex-col gap-y-13 xl:gap-y-20">
            {content.map(({ image, video, header, body, link }) => (
              <div key={`card-${header}-${body}`} className="flex flex-col">
                {image && (
                  <Image
                    image={image}
                    imageClasses="w-full object-cover mb-7.5 xl:mb-10"
                  />
                )}

                {video && (
                  <InViewVideo
                    videoClasses="[&>div>video]:object-cover mb-7.5 xl:mb-10"
                    autoPlay
                    controls={false}
                    video={video}
                    hasInVideoPlayButton
                    inVideoPlayButtonPositionClassName="left-2.5 bottom-10 xl:bottom-15 xl:left-[20px]"
                    dataTestId="Tiled-Springboard-Drawer-Card"
                  />
                )}

                <p className="h5 mb-5">{header}</p>

                <p
                  className={classNames('body-two', {
                    'mb-7.5': link?.url && link?.name,
                  })}
                  dangerouslySetInnerHTML={{ __html: body }}
                />

                {link?.url && link?.name && (
                  <Button link={link} className="self-start" />
                )}
              </div>
            ))}
          </div>

          {footNote && (
            <p
              className="mt-20 text-xs xl:mt-[100px]"
              dangerouslySetInnerHTML={{ __html: footNote }}
            />
          )}
        </div>
      </Drawer>
    </>
  );
};

export default TiledSpringboardCard;
